import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { tileCacheService } from '../../services/tile-cache/tile-cache-service.js'
import { mapStorage } from '../../storage/index.js'
import { db } from '../../database/schema.js'
import type { OfflineMap } from '../../database/schema.js'
import type { TileCacheStats } from '../../services/tile-cache/tile-cache-types.js'

@customElement('cairn-storage-settings')
export class CairnStorageSettings extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    .section-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 16px 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .section-title .material-icons {
      font-size: 22px;
      font-family: 'Material Symbols Outlined';
      color: var(--cairn-primary-color, #3b82f6);
    }

    .storage-bar {
      background: #e2e8f0;
      border-radius: 6px;
      height: 12px;
      overflow: hidden;
      margin-bottom: 8px;
    }

    .storage-fill {
      height: 100%;
      background: var(--cairn-primary-color, #3b82f6);
      border-radius: 6px;
      transition: width 0.3s ease;
    }

    .storage-label {
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-bottom: 24px;
    }

    .subsection {
      margin-bottom: 24px;
    }

    .subsection-title {
      font-size: 15px;
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 8px 0;
    }

    .subsection-desc {
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 0 0 12px 0;
    }

    .download-item {
      display: flex;
      align-items: center;
      padding: 12px 0;
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .download-info {
      flex: 1;
      min-width: 0;
    }

    .download-name {
      font-size: 14px;
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
    }

    .download-detail {
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .download-size {
      font-size: 13px;
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
      margin-right: 12px;
      flex-shrink: 0;
    }

    .delete-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      cursor: pointer;
      padding: 6px;
      border-radius: 50%;
      color: var(--cairn-text-secondary-color, #64748b);
      flex-shrink: 0;
    }

    .delete-btn:hover {
      background: #fee2e2;
      color: #dc2626;
    }

    .delete-btn .material-icons {
      font-size: 20px;
      font-family: 'Material Symbols Outlined';
    }

    .clear-btn {
      padding: 8px 16px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 8px;
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-color, #1e293b);
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      font-family: inherit;
    }

    .clear-btn:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .clear-btn.danger {
      border-color: #fca5a5;
      color: #dc2626;
    }

    .clear-btn.danger:hover {
      background: #fee2e2;
    }

    .empty-state {
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
      padding: 8px 0;
    }

    .stat-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
    }

    .stat-label {
      font-size: 14px;
      color: var(--cairn-text-color, #1e293b);
    }

    .stat-value {
      font-size: 14px;
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
    }

    .divider {
      border: none;
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
      margin: 24px 0;
    }
  `

  @state() private stats: TileCacheStats | null = null
  @state() private downloads: OfflineMap[] = []
  @state() private deviceQuota = 0
  @state() private deviceUsage = 0
  @state() private dataCacheSize = 0
  @state() private loading = true

  override connectedCallback(): void {
    super.connectedCallback()
    void this.loadStats()
  }

  private async loadStats(): Promise<void> {
    this.loading = true
    try {
      const [stats, regionsResult] = await Promise.all([
        tileCacheService.getStorageStats(),
        mapStorage.getMapRegions(),
      ])
      this.stats = stats
      this.deviceQuota = stats.deviceQuota ?? 0
      this.deviceUsage = stats.deviceUsage ?? 0
      this.downloads = regionsResult.success && regionsResult.data ? regionsResult.data : []
      this.dataCacheSize = await this.estimateDataCacheSize()
    } finally {
      this.loading = false
    }
  }

  private async estimateDataCacheSize(): Promise<number> {
    let total = 0
    const tables = [
      db.weatherCache,
      db.avalancheCache,
      db.emergencyCache,
      db.wildlifeCache,
      db.climbingCache,
    ]
    for (const table of tables) {
      total += await table.count()
    }
    return total * 5_000
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
  }

  private formatDate(iso: string): string {
    if (!iso) return 'Never'
    return new Date(iso).toLocaleDateString()
  }

  private expiryLabel(region: OfflineMap): string {
    if (!region.expiresAt) return 'Never expires'
    const d = new Date(region.expiresAt)
    if (d < new Date()) return 'Expired'
    return `Expires ${this.formatDate(region.expiresAt)}`
  }

  private async handleDeleteDownload(region: OfflineMap): Promise<void> {
    await mapStorage.deleteRegion(region.id)
    await this.loadStats()
  }

  private async handleClearBrowsingCache(): Promise<void> {
    await tileCacheService.clearUnpinned()
    await this.loadStats()
  }

  private async handleClearDataCaches(): Promise<void> {
    await Promise.all([
      db.weatherCache.clear(),
      db.avalancheCache.clear(),
      db.emergencyCache.clear(),
      db.wildlifeCache.clear(),
      db.climbingCache.clear(),
    ])
    await this.loadStats()
  }

  override render(): unknown {
    if (this.loading) {
      return html`
        <h2 class="section-title">
          <span class="material-icons">storage</span>
          Storage
        </h2>
        <p class="subsection-desc">Loading storage information...</p>
      `
    }

    const usagePct =
      this.deviceQuota > 0 ? Math.min(100, (this.deviceUsage / this.deviceQuota) * 100) : 0

    return html`
      <h2 class="section-title">
        <span class="material-icons">storage</span>
        Storage
      </h2>

      ${this.deviceQuota > 0
        ? html`
            <div class="storage-bar">
              <div class="storage-fill" style="width: ${usagePct}%"></div>
            </div>
            <div class="storage-label">
              ${this.formatBytes(this.deviceUsage)} of ${this.formatBytes(this.deviceQuota)} used
              (${usagePct.toFixed(0)}%)
            </div>
          `
        : nothing}

      <!-- Downloads -->
      <div class="subsection">
        <div class="subsection-title">Downloads</div>
        <div class="subsection-desc">Pinned map tiles from plan downloads. Not auto-evicted.</div>
        ${this.stats
          ? html`
              <div class="stat-row">
                <span class="stat-label">Total pinned</span>
                <span class="stat-value">${this.formatBytes(this.stats.pinnedBytes)}</span>
              </div>
            `
          : nothing}
        ${this.downloads.length === 0
          ? html`<div class="empty-state">No downloads yet.</div>`
          : this.downloads.map(
              r => html`
                <div class="download-item">
                  <div class="download-info">
                    <div class="download-name">${r.name}</div>
                    <div class="download-detail">
                      ${this.formatDate(r.downloadedAt)} &middot; ${this.expiryLabel(r)}
                      ${r.sourceIds ? html` &middot; ${r.sourceIds.length} layers` : nothing}
                    </div>
                  </div>
                  <span class="download-size">${this.formatBytes(r.size)}</span>
                  <button
                    class="delete-btn"
                    title="Delete download"
                    @click=${(): Promise<void> => this.handleDeleteDownload(r)}
                  >
                    <span class="material-icons">delete</span>
                  </button>
                </div>
              `
            )}
      </div>

      <hr class="divider" />

      <!-- Browsing cache -->
      <div class="subsection">
        <div class="subsection-title">Browsing cache</div>
        <div class="subsection-desc">
          Tiles cached from normal browsing. Auto-managed via LRU eviction.
        </div>
        ${this.stats
          ? html`
              <div class="stat-row">
                <span class="stat-label">Cached tiles</span>
                <span class="stat-value">${this.formatBytes(this.stats.unpinnedBytes)}</span>
              </div>
              <div class="stat-row">
                <span class="stat-label">Budget</span>
                <span class="stat-value">${this.formatBytes(this.stats.budgetBytes)}</span>
              </div>
            `
          : nothing}
        <button class="clear-btn" @click=${(): Promise<void> => this.handleClearBrowsingCache()}>
          Clear browsing cache
        </button>
      </div>

      <hr class="divider" />

      <!-- Data caches -->
      <div class="subsection">
        <div class="subsection-title">Data caches</div>
        <div class="subsection-desc">
          Weather, avalanche, emergency, wildlife, and climbing data.
        </div>
        <div class="stat-row">
          <span class="stat-label">Estimated size</span>
          <span class="stat-value">~${this.formatBytes(this.dataCacheSize)}</span>
        </div>
        <button
          class="clear-btn danger"
          @click=${(): Promise<void> => this.handleClearDataCaches()}
        >
          Clear all data caches
        </button>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-storage-settings': CairnStorageSettings
  }
}
